import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type {
  GetTransmittalDataByIdResponse,
  GetTransmittalFileVersionsForSpaceResponse,
  IsTransmittal403Response,
} from "@ServerTypes/transmittals/transmittals.schema";

type TransmittalByIdSuccess = Extract<GetTransmittalDataByIdResponse, { status: true }>;
type TransmittalAccessSuccess = Extract<IsTransmittal403Response, { status: true }>;
type TransmittalDetailsState = TransmittalByIdSuccess["data"] | Record<string, never>;
type TransmittalMetadataState = TransmittalByIdSuccess["metadata"] | Record<string, never>;
/**
 * The slice holds `/isTransmittal403`'s SUCCESS body once the call lands; before it lands the same
 * two members with `status: false` seed the store. That seed is NOT the route's failure arm — the
 * failure arm carries no `canViewTransmittal` and nothing dispatches it — hence `status: boolean`
 * rather than the response union, which also keeps `Exact<>` below working on a single object type.
 */
type TransmittalAccessState = { status: boolean; canViewTransmittal: TransmittalAccessSuccess["canViewTransmittal"] };
type TransmittalVersionsState = GetTransmittalFileVersionsForSpaceResponse;
/**
 * `mergeTransmitalsData` is dispatched with values that may be absent, and a case reducer that
 * returns `undefined` is "no replacement" to Immer, so the slice is left untouched.
 */
type TransmittalDetailsPayload = TransmittalDetailsState | undefined;
/**
 * Nothing dispatches or selects this slice today, so its element shape is unproven; `unknown[]`
 * keeps it honest and forces whoever wires it up to narrow. `never[]` — what `initialState: []`
 * inferred — would have made every future dispatch impossible.
 */
type TransmittalReferencesState = unknown[];

const saveTransmitalSlice = createSlice({
  name: "transmittals",
  initialState: {} as TransmittalDetailsState,
  reducers: {
    setTransmitalsData: (_state, action: PayloadAction<TransmittalDetailsPayload>) => action.payload,
    // Merged into the draft rather than returned as a fresh object: the state type is a union with
    // `Record<string, never>` (the `{}` initial state), and a spread of two such unions has no
    // single spread type. Immer produces the same next state either way.
    mergeTransmitalsData: (state, action: PayloadAction<TransmittalDetailsPayload>) => {
      Object.assign(state, action.payload);
    },
  },
});

const transmittalMetadataSlice = createSlice({
  name: "transmittalMetadata",
  initialState: {} as TransmittalMetadataState,
  reducers: {
    setTransmitalMetadata: (_state, action: PayloadAction<TransmittalMetadataState>) => action.payload,
  },
});

// Annotated, not `satisfies`: `satisfies` keeps the LITERAL `{ status: false; canViewTransmittal:
// false }`, which becomes the slice's state type — so `setTransmittalAccess` could never store a
// `status: true` body. The annotation widens the seed to the declared state type.
const initialTransmittalAccess: TransmittalAccessState = { status: false, canViewTransmittal: false };

const transmittalAccessSlice = createSlice({
  name: "transmittalAccess",
  initialState: initialTransmittalAccess,
  reducers: {
    setTransmittalAccess: (_state, action: PayloadAction<TransmittalAccessState>) => action.payload,
  },
});

const transmittalDataForReviewsSlice = createSlice({
  name: "transmittalDataForReviews",
  // `boolean | null`, not the `null` that a bare `initialState: null` inferred: CreateTransmittal
  // dispatches `setTransmittalDataForReviews(true)` and FilesInReview dispatches `(null)`, so the
  // store really does hold a boolean. FilesInReview widened its own selector to `boolean | null`
  // and left a note that the fix belonged here — this is that fix.
  initialState: null as boolean | null,
  reducers: {
    setTransmittalDataForReviews: (_state, action: PayloadAction<boolean | null>) => action.payload,
  },
});

const transmittalReferencesSlice = createSlice({
  name: "transmittalReferences",
  initialState: [] as TransmittalReferencesState,
  reducers: {
    setTransmittalReferences: (_state, action: PayloadAction<TransmittalReferencesState>) => action.payload,
  },
});

const transmittalVersionsSlice = createSlice({
  name: "transmittalVersions",
  initialState: [] as TransmittalVersionsState,
  reducers: {
    setTransmittalVersions: (_state, action: PayloadAction<TransmittalVersionsState>) => action.payload,
  },
});

// Every slice above now declares its payload, so these action creators carry a real argument type.
// `setTransmittalAccess` alone is re-wrapped below, to add excess-property checking through a
// variable — which `Exact<>` can express and a plain `PayloadAction` cannot.
export const { setTransmitalsData, mergeTransmitalsData } = saveTransmitalSlice.actions;
export const { setTransmitalMetadata } = transmittalMetadataSlice.actions;
export const { setTransmittalDataForReviews } = transmittalDataForReviewsSlice.actions;
export const { setTransmittalReferences } = transmittalReferencesSlice.actions;
export const { setTransmittalVersions } = transmittalVersionsSlice.actions;

const { setTransmittalAccess: _setTransmittalAccessRaw } = transmittalAccessSlice.actions;

/**
 * Object-payload action creator wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setTransmittalAccess = <T extends Exact<TransmittalAccessState, T>>(payload: T) =>
  _setTransmittalAccessRaw(payload);

export const saveTransmital = saveTransmitalSlice.reducer;
export const saveTransmitalMetadata = transmittalMetadataSlice.reducer;
export const setTransmittalAccessReducer = transmittalAccessSlice.reducer;
export const setTransmittalDataForReviewsReducer = transmittalDataForReviewsSlice.reducer;
export const setTransmittalReferencesReducer = transmittalReferencesSlice.reducer;
export const setTransmittalVersionsReducer = transmittalVersionsSlice.reducer;
